/**
 * R-62/R-63/R-64 — per-section topical drift registry.
 * 10 report types × their top N substantive sections (chrome skipped).
 * Per-type budgets retain earlier coverage when sections are added.
 *
 * TODO(review): canonical descriptions and keyword lists are seeded from
 * section labels + report type descriptions — human review recommended.
 */
import type { ReportTypeId } from '../../config/reportTypes';
import { REPORT_TYPE_REGISTRY } from '../../config/reportTypes';
import { getSectionsForType } from '../../config/reportSections';
import { SECTION_SCHEMAS, type CompositeSectionId } from '../../config/sectionSchemas';

export interface SectionDriftRegistryEntry {
  reportTypeId: ReportTypeId;
  sectionId: string;
  /** Human-readable expectation for LLM judge + embedding reference seed. */
  canonicalDescription: string;
  requiredKeywords: string[];
  /** Section-specific vocabulary for the same required topical concept. */
  keywordAliases?: Record<string, string[]>;
  prohibitedKeywords: string[];
  /** Text embedded as the reference vector for cosine similarity (R-64). */
  embeddingReference: string;
  /** When true, registry row needs SME review before production thresholds. */
  needsReview?: boolean;
}

const CHROME_SECTION_IDS = new Set([
  'toc', 'blindSpots', 'appendix', 'end', 'references', 'extendedData',
  'radarChart', 'lcoeChart', 'climateProfile',
]);

/** Pad sparse report types to 8 registry rows (forward-compatible placeholders). */
const SUPPLEMENTAL_SECTIONS: Partial<Record<ReportTypeId, Array<{ sectionId: string; defaultLabel: string }>>> = {
  'custom-brief': [
    { sectionId: 'customBriefFindings', defaultLabel: 'Custom Brief Findings' },
    { sectionId: 'customBriefEvidence', defaultLabel: 'Custom Brief Evidence' },
    { sectionId: 'customBriefRedFlags', defaultLabel: 'Custom Brief Red Flags' },
    { sectionId: 'customBriefConfidence', defaultLabel: 'Custom Brief Confidence' },
    { sectionId: 'customBriefRiskSynthesis', defaultLabel: 'Custom Brief Risk Synthesis' },
    { sectionId: 'customBriefQuestionCoverage', defaultLabel: 'Custom Brief Question Coverage' },
  ],
  'executive-summary': [
    { sectionId: 'investmentVerdict', defaultLabel: 'Investment Verdict Roll-up' },
  ],
  feasibility: [
    { sectionId: 'execSummary', defaultLabel: 'Executive Summary Roll-up' },
    { sectionId: 'constructabilityRollup', defaultLabel: 'Constructability Roll-up' },
    { sectionId: 'costRangeRollup', defaultLabel: 'Cost Range Roll-up' },
    { sectionId: 'riskMatrixRollup', defaultLabel: 'Risk Matrix Roll-up' },
    { sectionId: 'recommendationsRollup', defaultLabel: 'Recommendations Roll-up' },
  ],
  'test-fit': [
    { sectionId: 'constructabilityRollup', defaultLabel: 'Constructability Roll-up' },
    { sectionId: 'costRangeRollup', defaultLabel: 'Cost Range Roll-up' },
  ],
};

/** Manual overrides — SWC topography drift example + high-signal sections. */
const ENTRY_OVERRIDES: Partial<Record<`${ReportTypeId}:${string}`, Partial<SectionDriftRegistryEntry>>> = {
  'test-fit:topography': {
    requiredKeywords: ['elevation', 'slope', 'topography', 'grading', 'setback'],
    prohibitedKeywords: ['water rights', 'groundwater'],
    canonicalDescription:
      'Topography & Slopes — elevation contours, grading feasibility, slope constraints, and setback compliance for buildable envelope analysis.',
    embeddingReference:
      'Site topography section covering elevation, slope analysis, grading requirements, and setback compliance for data center layout.',
    needsReview: false,
  },
  'feasibility:gridStudy': {
    requiredKeywords: ['grid', 'substation', 'transmission', 'interconnection', 'utility', 'capacity'],
    prohibitedKeywords: ['wetland', 'NEPA', 'gas turbine lead time'],
  },
  'feasibility:projectIntentOverview': {
    // Known and explicitly unknown workloads/load boundaries describe intent
    // without repeating this section's heading. Keep the six-concept budget.
    keywordAliases: {
      intent: ['workload profile', 'target workload'],
      scope: ['undecided capacity', 'planned deployment', 'target timeline'],
      objective: ['screening purpose', 'project purpose', 'deployment purpose'],
    },
  },
  'feasibility:sitingNotes': {
    // The section covers land-use and physical constraints. Inflections and
    // concrete constraints are on-topic even without repeating the heading.
    keywordAliases: {
      siting: ['sited'],
      site: ['selected boundary', 'parcel boundary'],
      location: ['located'],
      zoning: ['zoned', 'land use'],
      constraint: ['floodplain', 'flood risk', 'flood zone', 'soil', 'drainage', 'setback', 'easement'],
      weather: ['hurricane', 'tornado', 'storm'],
    },
  },
  'environmental:nepaScreening': {
    requiredKeywords: ['NEPA', 'CEQA', 'environmental', 'screening', 'permit', 'review'],
    prohibitedKeywords: ['data hall', 'parking layout', 'gas turbine'],
  },
  'supply-chain-risk:supplyChainRisks': {
    requiredKeywords: ['supply', 'chain', 'lead', 'time', 'OEM', 'procurement', 'risk'],
    prohibitedKeywords: ['wetland', 'FEMA flood', 'stakeholder sentiment'],
  },
  'public-sentiment:stakeholderMap': {
    requiredKeywords: ['stakeholder', 'community', 'public', 'engagement', 'sentiment'],
    prohibitedKeywords: ['substation capacity', 'LCOE', 'setback compliance'],
  },
  'water-resource:waterRights': {
    requiredKeywords: ['water', 'rights', 'permit', 'withdrawal', 'allocation'],
    prohibitedKeywords: ['fiber latency', 'data hall orientation'],
  },
  'interconnection:latencyModel': {
    requiredKeywords: ['latency', 'cloud', 'region', 'network', 'round', 'trip'],
    prohibitedKeywords: ['wetland acreage', 'stormwater detention'],
  },
  'power-availability:interconnectionQueue': {
    requiredKeywords: ['interconnection', 'queue', 'LGIA', 'SGIA', 'utility', 'study'],
    prohibitedKeywords: ['stakeholder opposition', 'cultural resources'],
  },
  'custom-brief:projectContext': {
    requiredKeywords: ['project', 'context', 'site', 'intent', 'scope'],
    prohibitedKeywords: ['reviewer feedback', 'please revise'],
  },
  'executive-summary:crossReportSynthesis': {
    requiredKeywords: ['synthesis', 'cross', 'report', 'domain', 'finding', 'verdict'],
    prohibitedKeywords: ['please update', 'reviewer comments'],
  },
  'feasibility:execSummary': {
    requiredKeywords: ['finding', 'verdict', 'recommendation', 'executive'],
    canonicalDescription:
      'Executive Summary roll-up — top findings, investment verdict, and strategic recommendation synthesized from domain reports.',
  },
  'feasibility:constructabilityRollup': {
    requiredKeywords: ['constructability', 'readiness', 'constraint', 'phasing'],
    canonicalDescription:
      'Constructability roll-up — site readiness, construction constraints, and phasing plan.',
  },
  'feasibility:costRangeRollup': {
    requiredKeywords: ['cost', 'CAPEX', 'range', 'estimate'],
    canonicalDescription:
      'Cost range roll-up — CAPEX band, cost drivers, and sensitivity notes.',
  },
  'feasibility:riskMatrixRollup': {
    requiredKeywords: ['risk', 'matrix', 'severity', 'mitigation'],
    canonicalDescription:
      'Risk matrix roll-up — severity × likelihood grid and mitigations.',
  },
  'feasibility:recommendationsRollup': {
    requiredKeywords: ['recommendation', 'priority', 'action'],
    canonicalDescription:
      'Recommendations graph roll-up — priority actions and cross-domain dependencies.',
  },
};

/** DAF-19 — composite section schemas wired for H1 drift keyword seeds. */
const COMPOSITE_SCHEMA_KEYWORD_SEEDS: Partial<Record<CompositeSectionId, string[]>> = Object.fromEntries(
  Object.values(SECTION_SCHEMAS).map((s) => [
    s.sectionId,
    [...s.canonicalHeadings, ...s.requiredSubsections].map((t) => t.toLowerCase()),
  ]),
) as Partial<Record<CompositeSectionId, string[]>>;

const SECTION_KEYWORD_SEEDS: Record<string, string[]> = {
  // Topical vocabulary a real exec-summary PROSE body contains — NOT the
  // structural meta-labels ('executive','summary','verdict') that a narrative
  // never literally repeats, which produced a guaranteed 0% overlap →
  // false-positive block on every report (report-gate-rootcause-fixplan STEP 6).
  // Keeps a meaningful topical-drift check (catches a genuinely off-topic exec
  // summary) without the structural-label false positive. 'recommend' lemma
  // matches recommend/recommended/recommendation.
  executiveSummary: ['site', 'project', 'power', 'capacity', 'risk', 'recommend', 'phase', 'generation'],
  projectIntentOverview: ['project', 'intent', 'overview', 'scope', 'objective'],
  btmOverview: ['BTM', 'behind', 'meter', 'technology', 'generation'],
  gridStudy: ['grid', 'power', 'substation', 'transmission', 'utility'],
  transportStudy: ['transport', 'logistics', 'access', 'highway', 'rail'],
  connectivityStudy: ['connectivity', 'fiber', 'network', 'carrier', 'bandwidth'],
  sitingNotes: ['siting', 'site', 'location', 'constraint', 'zoning'],
  climateSevereWeather: ['climate', 'weather', 'temperature', 'storm', 'severe'],
  nepaScreening: ['NEPA', 'CEQA', 'environmental', 'screening'],
  ecologicalAssessment: ['ecological', 'habitat', 'species', 'wetland', 'wildlife'],
  waterResources: ['water', 'resource', 'hydrology', 'aquifer', 'supply'],
  airQualityEmissions: ['air', 'quality', 'emission', 'attainment', 'pollutant'],
  floodNaturalHazards: ['flood', 'FEMA', 'hazard', 'natural', 'risk'],
  culturalHistorical: ['cultural', 'historical', 'tribal', 'archaeological'],
  landUseZoning: ['land', 'use', 'zoning', 'ordinance', 'permitted'],
  noiseVisualImpact: ['noise', 'visual', 'impact', 'aesthetic'],
  mitigationPlan: ['mitigation', 'measure', 'compensatory', 'restoration'],
  parcelOverview: ['parcel', 'acreage', 'buildable', 'footprint', 'area'],
  topography: ['elevation', 'slope', 'topography', 'grading', 'terrain'],
  buildingPlacement: ['building', 'placement', 'orientation', 'layout', 'data hall'],
  setbackCompliance: ['setback', 'compliance', 'buffer', 'yard', 'requirement'],
  parkingAccess: ['parking', 'access', 'emergency', 'fire', 'lane'],
  stormwater: ['stormwater', 'detention', 'drainage', 'runoff', 'retention'],
  utilityRouting: ['utility', 'routing', 'feed', 'conduit', 'easement'],
  phasedBuildout: ['phase', 'buildout', 'expansion', 'sequencing', 'timeline'],
  maxCapacity: ['capacity', 'maximum', 'campus', 'MW', 'ceiling'],
  powerStrategyContext: ['power', 'strategy', 'requirement', 'load', 'demand'],
  gridCapacity: ['grid', 'capacity', 'substation', 'transformer', 'feeder'],
  interconnectionQueue: ['interconnection', 'queue', 'LGIA', 'study', 'utility'],
  rateStructures: ['rate', 'tariff', 'utility', 'demand', 'charge'],
  curtailmentRisk: ['curtailment', 'risk', 'dispatch', 'constraint', 'renewable'],
  renewableIntegration: ['renewable', 'solar', 'wind', 'integration', 'on-site'],
  supplyAvailability: ['supply', 'availability', 'demand', 'municipal', 'capacity'],
  groundwater: ['groundwater', 'aquifer', 'well', 'pumping', 'recharge'],
  waterRights: ['water', 'rights', 'permit', 'allocation', 'priority'],
  droughtResilience: ['drought', 'resilience', 'stress', 'scarcity', 'contingency'],
  coolingByTech: ['cooling', 'water', 'consumption', 'technology', 'BTM'],
  wastewaterStormwater: ['wastewater', 'stormwater', 'discharge', 'NPDES', 'treatment'],
  fiberRings: ['fiber', 'availability', 'route', 'ring', 'mile'],
  latencyModel: ['latency', 'cloud', 'region', 'milliseconds', 'round'],
  carrierDiversity: ['carrier', 'diversity', 'redundancy', 'provider', 'path'],
  ixProximity: ['exchange', 'peering', 'IX', 'proximity', 'internet'],
  submarineCable: ['submarine', 'cable', 'landing', 'coastal', 'transoceanic'],
  networkResilience: ['resilience', 'BGP', 'DDoS', 'redundancy', 'uptime'],
  procurementOverview: ['procurement', 'overview', 'BoM', 'equipment', 'vendor'],
  criticalPathProcurement: ['critical', 'path', 'lead', 'time', 'delivery'],
  supplyChainRisks: ['supply', 'chain', 'risk', 'shortage', 'delay'],
  supplierRiskRanking: ['supplier', 'risk', 'ranking', 'OEM', 'vendor'],
  quoteComparisons: ['quote', 'comparison', 'pricing', 'vendor', 'cost'],
  supplyChainRiskMatrix: ['risk', 'matrix', 'severity', 'likelihood', 'mitigation'],
  executive: ['executive', 'summary', 'sentiment', 'stakeholder', 'verdict'],
  stakeholderMap: ['stakeholder', 'map', 'community', 'agency', 'interest'],
  perBtmSentiment: ['BTM', 'sentiment', 'technology', 'reaction', 'support'],
  themeDeepDives: ['theme', 'deep', 'dive', 'narrative', 'concern'],
  trendAnalysis: ['trend', 'analysis', 'timeline', 'coverage', 'shift'],
  comparableSiteBenchmark: ['comparable', 'benchmark', 'peer', 'site', 'reference'],
  mitigationPlaybook: ['mitigation', 'playbook', 'outreach', 'engagement', 'response'],
  sentimentResearchDetail: ['sentiment', 'research', 'source', 'coverage', 'media'],
  projectContext: ['project', 'context', 'scope', 'question', 'diligence'],
  btmViability: ['BTM', 'viability', 'energy', 'path', 'assessment'],
  crossReportSynthesis: ['synthesis', 'cross', 'report', 'domain', 'finding'],
  criticalRisks: ['critical', 'risk', 'blocker', 'showstopper', 'constraint'],
  actionPlan: ['action', 'plan', 'next', 'step', 'recommendation'],
  sourceReports: ['source', 'report', 'reference', 'lineage', 'input'],
  customBriefFindings: ['findings', 'answer', 'question', 'diligence', 'evidence'],
  customBriefEvidence: ['evidence', 'citation', 'source', 'document', 'support'],
  customBriefRedFlags: ['red flag', 'risk', 'concern', 'blocker', 'issue'],
  customBriefConfidence: ['confidence', 'certainty', 'high', 'medium', 'low'],
  customBriefRiskSynthesis: ['synthesis', 'risk', 'theme', 'pattern', 'summary'],
  customBriefQuestionCoverage: ['question', 'coverage', 'answered', 'diligence', 'brief'],
  investmentVerdict: ['verdict', 'investment', 'recommendation', 'viability', 'decision'],
  constructabilityAssessment: ['constructability', 'build', 'construction', 'feasibility'],
  costRangeEstimate: ['cost', 'range', 'estimate', 'CAPEX', 'budget'],
  riskMatrix: ['risk', 'matrix', 'severity', 'likelihood', 'impact'],
  execSummary: ['executive', 'summary', 'finding', 'verdict', 'recommendation'],
  constructabilityRollup: ['constructability', 'readiness', 'construction', 'phasing', 'constraint'],
  costRangeRollup: ['cost', 'CAPEX', 'range', 'estimate', 'contingency'],
  riskMatrixRollup: ['risk', 'matrix', 'severity', 'likelihood', 'mitigation'],
  recommendationsRollup: ['recommendation', 'priority', 'action', 'dependency', 'next step'],
  permittingPathway: ['permit', 'pathway', 'approval', 'timeline', 'agency'],
  environmentalRiskMatrix: ['environmental', 'risk', 'matrix', 'impact'],
  powerRiskMatrix: ['power', 'risk', 'matrix', 'grid', 'utility'],
  waterRiskMatrix: ['water', 'risk', 'matrix', 'scarcity', 'supply'],
  connectivityRiskMatrix: ['connectivity', 'risk', 'matrix', 'network'],
};

const CROSS_TYPE_PROHIBITED: Partial<Record<ReportTypeId, string[]>> = {
  'test-fit': ['water rights', 'groundwater', 'stakeholder opposition', 'gas turbine supply'],
  feasibility: ['reviewer feedback', 'please revise', 'gas turbine lead time'],
  environmental: ['data hall orientation', 'parking layout', 'latency model'],
  'supply-chain-risk': ['wetland delineation', 'FEMA flood zone', 'NEPA screening'],
  'public-sentiment': ['substation capacity', 'LCOE breakdown', 'setback compliance'],
  interconnection: ['wetland acreage', 'stormwater detention', 'NEPA'],
  'water-resource': ['fiber latency', 'BGP routing', 'data hall'],
  'power-availability': ['cultural resources', 'stakeholder map'],
  'custom-brief': ['reviewer feedback', 'please revise'],
  'executive-summary': ['reviewer comments', 'please update'],
};

function labelTokens(label: string): string[] {
  return label
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter((t) => t.length >= 3);
}

function buildEntry(
  reportTypeId: ReportTypeId,
  sectionId: string,
  defaultLabel: string,
): SectionDriftRegistryEntry {
  const typeDef = REPORT_TYPE_REGISTRY[reportTypeId];
  const overrideKey = `${reportTypeId}:${sectionId}` as `${ReportTypeId}:${string}`;
  const override = ENTRY_OVERRIDES[overrideKey];

  const seeded = SECTION_KEYWORD_SEEDS[sectionId]
    ?? COMPOSITE_SCHEMA_KEYWORD_SEEDS[sectionId as CompositeSectionId]
    ?? labelTokens(defaultLabel);
  const requiredKeywords = override?.requiredKeywords ?? [...new Set([...seeded, ...labelTokens(defaultLabel)])].slice(0, 8);
  const prohibitedKeywords = override?.prohibitedKeywords
    ?? CROSS_TYPE_PROHIBITED[reportTypeId]
    ?? ['reviewer feedback', 'please revise'];

  const canonicalDescription = override?.canonicalDescription
    ?? `${defaultLabel} — ${typeDef.description}`;

  const embeddingReference = override?.embeddingReference ?? canonicalDescription;

  return {
    reportTypeId,
    sectionId,
    canonicalDescription,
    requiredKeywords,
    ...(override?.keywordAliases ? { keywordAliases: override.keywordAliases } : {}),
    prohibitedKeywords,
    embeddingReference,
    needsReview: override?.needsReview ?? true,
  };
}

/**
 * Per-type registry budget. Default 8 — the "top 8 substantive sections" this
 * file is documented around, and the value every type but one still uses.
 *
 * STAGE 5.1 — feasibility is the exception, and the reason is a REGRESSION this
 * budget would otherwise hide. The pick is POSITIONAL (`slice`), so absorbing
 * the six grid-screening sections after `gridStudy` pushed `transportStudy`,
 * `sitingNotes` and `riskAssessment` out of feasibility's window — three
 * sections that had R-62/63/64 drift protection before the move and would have
 * silently lost it. The golden audit fixture caught exactly that: it lost its
 * `keyword:sitingNotes` finding.
 *
 * 14 = the original 8 + the 6 sections absorbed in Stage 5.1. It is deliberately
 * NOT "all 16": `actionPlan` and `conclusion` were outside the window before
 * this change and stay outside it, so this restores prior coverage and extends
 * it to the moved sections WITHOUT newly drift-checking synthesis sections that
 * legitimately range across topics. No other report type is affected.
 */
const REGISTRY_BUDGET_BY_TYPE: Partial<Record<ReportTypeId, number>> = {
  // The frozen water disclosure adds a row without displacing an existing
  // risk/cost section from these positional windows.
  feasibility: 19,
  'power-availability': 9,
  'water-resource': 9,
};
const DEFAULT_REGISTRY_BUDGET = 8;

function pickSubstantiveSections(reportTypeId: ReportTypeId): Array<{ sectionId: string; defaultLabel: string }> {
  const budget = REGISTRY_BUDGET_BY_TYPE[reportTypeId] ?? DEFAULT_REGISTRY_BUDGET;
  const sections = getSectionsForType(reportTypeId);
  const substantive = sections.filter((s) => !CHROME_SECTION_IDS.has(s.sectionId));
  const picked = substantive.slice(0, budget).map((s) => ({ sectionId: s.sectionId, defaultLabel: s.defaultLabel }));
  if (picked.length >= budget) return picked;
  const pad = SUPPLEMENTAL_SECTIONS[reportTypeId] ?? [];
  for (const extra of pad) {
    if (picked.length >= budget) break;
    if (!picked.some((p) => p.sectionId === extra.sectionId)) picked.push(extra);
  }
  return picked;
}

function buildRegistry(): SectionDriftRegistryEntry[] {
  const typeIds = Object.keys(REPORT_TYPE_REGISTRY) as ReportTypeId[];
  const entries: SectionDriftRegistryEntry[] = [];
  for (const reportTypeId of typeIds) {
    for (const { sectionId, defaultLabel } of pickSubstantiveSections(reportTypeId)) {
      entries.push(buildEntry(reportTypeId, sectionId, defaultLabel));
    }
  }
  return entries;
}

export const SECTION_DRIFT_REGISTRY: SectionDriftRegistryEntry[] = buildRegistry();

export function registryKey(reportTypeId: ReportTypeId, sectionId: string): string {
  return `${reportTypeId}:${sectionId}`;
}

export function getRegistryEntry(
  reportTypeId: ReportTypeId,
  sectionId: string,
): SectionDriftRegistryEntry | undefined {
  return SECTION_DRIFT_REGISTRY.find(
    (e) => e.reportTypeId === reportTypeId && e.sectionId === sectionId,
  );
}

export function getRegistryEntriesForType(reportTypeId: ReportTypeId): SectionDriftRegistryEntry[] {
  return SECTION_DRIFT_REGISTRY.filter((e) => e.reportTypeId === reportTypeId);
}
